import {
	ChartCombinedIcon,
	CycleIcon,
	FormsIcon,
	HierarchiesIcon,
	HrFlagIcon,
	LogoutIcon,
	ManageEmailsIcon,
	OrganizationChartIcon,
	QuestionTemplateIcon,
	RolesIcon,
	SettingsIcon,
	Sidebar as SidebarBase,
	UsersIcon,
} from '@viapeople/components'
import type React from 'react'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import logoGray from '/logo-grayscale.png'
import { useToast } from '../../utils/ToastContext'
import { useAdminSidebarStore } from './store'

interface SidebarProps {
	logoSrc?: string
	defaultCollapsed?: boolean
	selectedMenuItem?: string
	onCollapsedChange?: (collapsed: boolean) => void
}

type ActionId =
	| 'organizations'
	| 'users'
	| 'roles'
	| 'hierarchies'
	| 'permission-templates'
	| 'forms'
	| 'emails'
	| 'cycles'
	| 'deal-team-aggregation'
	| 'integrations'
	| 'sftp'
	| 'settings'
	| 'hr-review-flags'
	| 'logout'

interface MenuItem {
	id: string
	label: string
	href: string
	badge?: number
}

function resolveIcon(item: MenuItem) {
	const iconMap: Record<ActionId, React.FC<{ size?: number; className?: string }>> = {
		organizations: OrganizationChartIcon,
		users: UsersIcon,
		roles: RolesIcon,
		hierarchies: HierarchiesIcon,
		'permission-templates': QuestionTemplateIcon,
		forms: FormsIcon,
		emails: ManageEmailsIcon,
		cycles: CycleIcon,
		'deal-team-aggregation': ChartCombinedIcon,
		integrations: SettingsIcon,
		sftp: SettingsIcon,
		settings: SettingsIcon,
		'hr-review-flags': HrFlagIcon,
		logout: LogoutIcon,
	}

	// Default to CycleIcon for unknown action IDs (should not happen in practice)
	const icon = iconMap[item.id as ActionId] || CycleIcon
	return {
		...item,
		icon,
	}
}

const Sidebar: React.FC<SidebarProps> = ({ defaultCollapsed = true, onCollapsedChange }) => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const { menuItems, bottomActions, error, initialize, clearError } = useAdminSidebarStore()
	const { showToast } = useToast()

	// Initialize sidebar when organizationId changes. The HR Review Flags
	// entry is org-scoped — it surfaces whenever the user is a Cycle Admin
	// on at least one cycle in this org.
	useEffect(() => {
		initialize(organizationId)
	}, [organizationId, initialize])

	// Show error toast
	useEffect(() => {
		if (error) {
			showToast(error, 'error')
			clearError()
		}
	}, [error, showToast, clearError])

	return (
		<SidebarBase
			logoSrc={logoGray}
			menuItems={menuItems.map(resolveIcon)}
			bottomActions={bottomActions.map(resolveIcon)}
			defaultCollapsed={defaultCollapsed}
			onCollapsedChange={onCollapsedChange}
		/>
	)
}

export default Sidebar
